import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Circle, CircleMarker, Popup, Tooltip, LayersControl, useMap } from 'react-leaflet';
import { KECAMATAN_COORDS } from '../lib/kecamatanCoords';
import { statsApi } from '../lib/api';
import { formatNumber } from '../components/shared/UI';
import { Filter, Layers, Search, MapPin, Users, ArrowRight, UserCheck, ShieldCheck, Trophy, Sparkles } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { STRENGTH_LEVELS, NO_DATA_COLOR, NO_DATA_LABEL, strengthColor, strengthLabel } from '../lib/strengthColors';
import { MASTER_DAPIL_DATA } from '../lib/dapilMasterData';

const COORDS_BY_NAME = Object.fromEntries(KECAMATAN_COORDS.map(k => [k.name, k.coords]));

// Helper Map Fly To
const MapFlyTo = ({ coords }) => {
  const map = useMap();
  useEffect(() => {
    if (coords) {
      map.flyTo(coords, 12, { animate: true, duration: 1 });
    }
  }, [coords, map]);
  return null;
};

const pctOf = (k) => k?.target ? Math.round((k.realisasi / k.target) * 100) : 0;
const hasDataOf = (k) => k && k.target > 0;

// Cari Dapil untuk setiap Kecamatan
const getDapilForKecamatan = (kecName) => {
  for (const d of MASTER_DAPIL_DATA) {
    if (d.kecamatan.some(k => k.nama.toLowerCase() === (kecName || '').toLowerCase())) {
      return d.dapil;
    }
  }
  return 'Kabupaten';
};

const SebaranPeta = () => {
  const navigate = useNavigate();
  const [kecList, setKecList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchKec, setSearchKec] = useState('');
  const [filterDapil, setFilterDapil] = useState('all');
  const [selectedKecName, setSelectedKecName] = useState(null);

  useEffect(() => {
    statsApi.kecamatanDetail()
      .then(r => {
        setKecList(r.data || []);
        if (r.data && r.data.length > 0) {
          setSelectedKecName(r.data[0].kecamatan);
        }
      })
      .catch(() => setKecList([]))
      .finally(() => setLoading(false));
  }, []);

  const pinned = useMemo(() => {
    return kecList.filter(k => COORDS_BY_NAME[k.kecamatan]);
  }, [kecList]);

  // Filter daftar kecamatan di sisi kiri
  const filteredKecList = useMemo(() => {
    return kecList.filter(k => {
      const matchSearch = (k.kecamatan || '').toLowerCase().includes(searchKec.toLowerCase());
      const kDapil = getDapilForKecamatan(k.kecamatan);
      const matchDapil = filterDapil === 'all' || kDapil === filterDapil;
      return matchSearch && matchDapil;
    });
  }, [kecList, searchKec, filterDapil]);

  // Kecamatan yang sedang dipilih
  const selectedKec = useMemo(() => {
    return kecList.find(k => k.kecamatan === selectedKecName) || kecList[0] || null;
  }, [kecList, selectedKecName]);

  const selectedCoords = selectedKec ? COORDS_BY_NAME[selectedKec.kecamatan] : null;

  return (
    <div className="space-y-6">
      {/* 6 KARTU STAT KEKUATAN WILAYAH DI ATAS */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          ...STRENGTH_LEVELS.map((lvl, i) => ({
            c: lvl.c,
            l: lvl.l,
            v: pinned.filter(k => {
              if (!hasDataOf(k)) return false;
              const p = pctOf(k);
              const max = i > 0 ? STRENGTH_LEVELS[i - 1].min : Infinity;
              return p >= lvl.min && p < max;
            }).length,
          })),
          { c: NO_DATA_COLOR, l: NO_DATA_LABEL, v: pinned.filter(k => !hasDataOf(k)).length },
        ].map(s => (
          <div key={s.l} className="bg-white rounded-2xl p-4 card-shadow border border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${s.c}20` }}>
              <span className="w-4 h-4 rounded-full" style={{ background: s.c }}></span>
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-bold text-gray-500 truncate">{s.l}</p>
              <h3 className="text-xl font-black text-gray-900 leading-tight">
                {s.v} <span className="text-[10px] font-semibold text-gray-400">Kec</span>
              </h3>
            </div>
          </div>
        ))}
      </div>

      {/* 2-KOLOM UTAMA: LIST KECAMATAN (KIRI) & PETA + DETAIL (KANAN) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* KOLOM KIRI: FILTER & DAFTAR KECAMATAN (lg:col-span-4) */}
        <div className="lg:col-span-4 bg-white rounded-2xl p-5 card-shadow border border-gray-100 flex flex-col h-[750px]">
          <div className="mb-4">
            <h3 className="font-extrabold text-base text-gray-900">Persebaran Wilayah</h3>
            <p className="text-xs text-gray-500 font-medium">Pilih kecamatan untuk fokus peta dan rincian partisipan</p>
          </div>

          {/* Search Box */}
          <div className="relative mb-3">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={searchKec}
              onChange={e => setSearchKec(e.target.value)}
              placeholder="Cari kecamatan..."
              className="pl-9 h-9 text-xs"
            />
          </div>

          {/* Filter Pills Dapil */}
          <div className="flex items-center gap-1 overflow-x-auto pb-2 mb-3">
            <button
              onClick={() => setFilterDapil('all')}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all flex-shrink-0 ${
                filterDapil === 'all'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-orange-50'
              }`}
            >
              Semua Dapil
            </button>
            {MASTER_DAPIL_DATA.map(d => (
              <button
                key={d.dapil}
                onClick={() => setFilterDapil(d.dapil)}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all flex-shrink-0 ${
                  filterDapil === d.dapil
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-orange-50'
                }`}
              >
                {d.dapil}
              </button>
            ))}
          </div>

          {/* Daftar Scrollable Kecamatan */}
          <div className="flex-1 overflow-y-auto space-y-1.5 pr-1">
            {loading ? (
              <p className="text-center text-xs text-gray-400 py-8 font-medium">Memuat kecamatan...</p>
            ) : filteredKecList.length === 0 ? (
              <p className="text-center text-xs text-gray-400 py-8 font-medium">Tidak ada kecamatan yang cocok.</p>
            ) : (
              filteredKecList.map(k => {
                const isSelected = selectedKecName === k.kecamatan;
                const totalOrang = (k.simpatisan || 0) + (k.pelopor_count || 0) + (k.non_pelopor_count || 0) + (k.saksi || 0);
                const pct = pctOf(k);
                const hasData = hasDataOf(k);
                const dapilName = getDapilForKecamatan(k.kecamatan);

                return (
                  <div
                    key={k.kecamatan}
                    onClick={() => setSelectedKecName(k.kecamatan)}
                    className={`p-3 rounded-xl border transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-orange-50/80 border-orange-400 shadow-sm ring-1 ring-orange-400'
                        : 'bg-white hover:bg-gray-50 border-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="font-extrabold text-xs text-gray-900">{k.kecamatan}</span>
                          <span className="text-[10px] text-gray-400 font-semibold">({dapilName})</span>
                        </div>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          <b className="text-gray-900">{formatNumber(totalOrang)}</b> orang terdaftar
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge
                          className="text-[10px] px-1.5 py-0 font-bold"
                          style={{
                            backgroundColor: `${strengthColor(pct, hasData)}20`,
                            color: strengthColor(pct, hasData),
                            borderColor: strengthColor(pct, hasData)
                          }}
                        >
                          {strengthLabel(pct, hasData)}
                        </Badge>
                        <p className="text-[10px] text-gray-400 font-semibold mt-0.5">{pct}% target</p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* KOLOM KANAN: PETA INTERAKTIF & PANEL DETAIL (lg:col-span-8) */}
        <div className="lg:col-span-8 space-y-4">
          {/* PETA INTERAKTIF */}
          <div className="bg-white rounded-2xl p-4 card-shadow border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-base font-extrabold text-gray-900">
                  Peta Kekuatan — {selectedKec?.kecamatan || 'Kabupaten Sukabumi'}
                </h3>
                <p className="text-xs text-gray-500 font-medium">
                  Area choropleth menandakan zona kekuatan basis pemilih di tiap kecamatan.
                </p>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden relative border border-gray-200" style={{ height: 430 }}>
              {loading ? (
                <div className="h-full flex items-center justify-center text-gray-400 font-semibold text-xs">Memuat peta...</div>
              ) : (
                <MapContainer center={[-7.05, 106.75]} zoom={10} style={{ height: '100%', width: '100%' }}>
                  <LayersControl position="topleft">
                    <LayersControl.BaseLayer checked name="Peta Standar (Google Maps)">
                      <TileLayer
                        url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                        attribution="&copy; Google Maps"
                      />
                    </LayersControl.BaseLayer>
                    <LayersControl.BaseLayer name="Peta Satelit (Google Hybrid)">
                      <TileLayer
                        url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
                        attribution="&copy; Google Maps"
                      />
                    </LayersControl.BaseLayer>
                    <LayersControl.BaseLayer name="Peta Jalan (Esri Street)">
                      <TileLayer
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
                        attribution="&copy; Esri &mdash; OpenStreetMap contributors"
                      />
                    </LayersControl.BaseLayer>
                  </LayersControl>

                  {/* Fly to selected kecamatan */}
                  {selectedCoords && <MapFlyTo coords={selectedCoords} />}

                  {/* Choropleth-like coverage circles */}
                  {pinned.map(k => {
                    const pct = pctOf(k);
                    const hasData = hasDataOf(k);
                    const isSelected = selectedKecName === k.kecamatan;
                    return (
                      <Circle
                        key={`c-${k.kecamatan}`}
                        center={COORDS_BY_NAME[k.kecamatan]}
                        radius={isSelected ? 6500 : 5000}
                        pathOptions={{
                          color: isSelected ? '#EA580C' : strengthColor(pct, hasData),
                          fillColor: strengthColor(pct, hasData),
                          fillOpacity: isSelected ? 0.55 : 0.35,
                          weight: isSelected ? 3 : 1.5
                        }}
                      />
                    );
                  })}

                  {/* Marker labels */}
                  {pinned.map(k => {
                    const pct = pctOf(k);
                    const hasData = hasDataOf(k);
                    const isSelected = selectedKecName === k.kecamatan;
                    return (
                      <CircleMarker
                        key={k.kecamatan}
                        center={COORDS_BY_NAME[k.kecamatan]}
                        radius={isSelected ? 11 : 8}
                        pathOptions={{
                          color: '#fff',
                          fillColor: strengthColor(pct, hasData),
                          fillOpacity: 1,
                          weight: isSelected ? 3 : 2
                        }}
                        eventHandlers={{
                          click: () => setSelectedKecName(k.kecamatan)
                        }}
                      >
                        <Tooltip direction="top" offset={[0, -8]} opacity={1}>
                          <b>{k.kecamatan}</b> — {strengthLabel(pct, hasData)} ({pct}%)
                        </Tooltip>
                        <Popup>
                          <div className="font-semibold text-xs min-w-[180px]">
                            <p className="font-extrabold text-sm mb-1 text-orange-600">{k.kecamatan}</p>
                            <div className="space-y-0.5 text-xs">
                              <p>Relawan: <b>{formatNumber(k.simpatisan)}</b></p>
                              <p>Pelopor: <b>{formatNumber(k.pelopor_count)}</b></p>
                              <p>Non Pelopor: <b>{formatNumber(k.non_pelopor_count)}</b></p>
                              <p>Saksi: <b>{formatNumber(k.saksi)}</b></p>
                            </div>
                          </div>
                        </Popup>
                      </CircleMarker>
                    );
                  })}
                </MapContainer>
              )}
            </div>

            {/* Horizontal legend at bottom */}
            <div className="mt-3 flex flex-wrap items-center justify-center gap-3 px-3 py-2 rounded-xl bg-orange-50/50 border border-orange-100">
              <span className="font-extrabold text-[11px] text-gray-800">KEKUATAN:</span>
              {[
                ...STRENGTH_LEVELS.map(lvl => ({ c: lvl.c, l: lvl.l })),
                { c: NO_DATA_COLOR, l: NO_DATA_LABEL },
              ].map(x => (
                <div key={x.l} className="flex items-center gap-1 text-[11px] font-semibold text-gray-700">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: x.c }}></span>
                  {x.l}
                </div>
              ))}
            </div>
          </div>

          {/* PANEL RINGKASAN WILAYAH TERPILIH DENGAN TOMBOL "LIHAT DAFTAR NAMA" */}
          {selectedKec && (
            <div className="bg-white rounded-2xl p-5 card-shadow border border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-orange-600" />
                    <h3 className="font-black text-lg text-gray-900">
                      Kecamatan {selectedKec.kecamatan}
                    </h3>
                    <Badge variant="outline" className="text-xs font-bold text-orange-700 bg-orange-50 border-orange-200">
                      {getDapilForKecamatan(selectedKec.kecamatan)}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 font-medium mt-0.5">
                    Rincian kader, relawan, dan saksi yang tercatat di wilayah ini
                  </p>
                </div>

                {/* Tombol Aksi: Lihat Daftar Nama */}
                <Button
                  onClick={() => navigate(`/partisipan?kecamatan=${encodeURIComponent(selectedKec.kecamatan)}`)}
                  className="bg-orange-600 hover:bg-orange-700 text-white font-bold text-xs gap-1.5 h-10 px-4 shadow-md"
                >
                  <Users className="w-4 h-4" />
                  <span>Lihat Daftar Nama</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </div>

              {/* Grid Metrik Kategori Orang di Kecamatan Terpilih */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div className="p-3 rounded-xl bg-orange-50/60 border border-orange-100">
                  <p className="text-[10px] font-bold text-gray-500 uppercase">Total Orang</p>
                  <h4 className="text-xl font-black text-gray-900 mt-0.5">
                    {formatNumber((selectedKec.simpatisan || 0) + (selectedKec.pelopor_count || 0) + (selectedKec.non_pelopor_count || 0) + (selectedKec.saksi || 0))}
                  </h4>
                  <p className="text-[10px] text-orange-600 font-semibold mt-0.5">Tercatat Aktif</p>
                </div>

                <div className="p-3 rounded-xl bg-amber-50/60 border border-amber-100">
                  <p className="text-[10px] font-bold text-gray-500 uppercase">Kader Pelopor</p>
                  <h4 className="text-xl font-black text-amber-700 mt-0.5">
                    {formatNumber(selectedKec.pelopor_count || 0)}
                  </h4>
                  <p className="text-[10px] text-gray-400 mt-0.5">Struktur Inti</p>
                </div>

                <div className="p-3 rounded-xl bg-yellow-50/60 border border-yellow-100">
                  <p className="text-[10px] font-bold text-gray-500 uppercase">Non Pelopor</p>
                  <h4 className="text-xl font-black text-yellow-800 mt-0.5">
                    {formatNumber(selectedKec.non_pelopor_count || 0)}
                  </h4>
                  <p className="text-[10px] text-gray-400 mt-0.5">Kader Wilayah</p>
                </div>

                <div className="p-3 rounded-xl bg-emerald-50/60 border border-emerald-100">
                  <p className="text-[10px] font-bold text-gray-500 uppercase">Saksi TPS</p>
                  <h4 className="text-xl font-black text-emerald-700 mt-0.5">
                    {formatNumber(selectedKec.saksi || 0)}
                  </h4>
                  <p className="text-[10px] text-emerald-600 font-semibold mt-0.5">Petugas TPS</p>
                </div>

                <div className="p-3 rounded-xl bg-blue-50/60 border border-blue-100 col-span-2 sm:col-span-1">
                  <p className="text-[10px] font-bold text-gray-500 uppercase">Relawan</p>
                  <h4 className="text-xl font-black text-blue-700 mt-0.5">
                    {formatNumber(selectedKec.simpatisan || 0)}
                  </h4>
                  <p className="text-[10px] text-blue-600 font-semibold mt-0.5">Simpatisan</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SebaranPeta;
